/**
 * How a route option reads on a card, once the engine has costed it.
 *
 * Kept apart from the panel because the wording is the part that has to be
 * right. A card is glanced at by someone driving or sitting beside someone
 * driving, and the two mistakes that matter are quoting a cost that is not real
 * and quoting a benefit the option does not deliver.
 */
import { RouteOption, SentinelAction } from '../../services/missionApi';
import { formatMeters, formatSpan } from './format';

export const CHAT_SPEAKER: Record<string, string> = {
  user: 'YOU',
  sentinel: 'SENTINEL',
  system: 'LIVE',
};

export function optionLabel(option: RouteOption): string {
  return option.label || `Option ${(option.index ?? 0) + 1}`;
}

/**
 * The cost of a route in the operator's terms.
 *
 * Signed and spelled out, because "+6m" against a plan is genuinely ambiguous
 * about which way is worse when the thing being avoided is a person. Under a
 * minute is not a cost worth naming: rounding forty seconds up would make a
 * free option look like a trade.
 */
export function optionCost(option: RouteOption): string {
  const delta = option.delta_duration_s;
  if (delta == null || !Number.isFinite(delta)) return '';
  if (Math.abs(delta) < 60) return 'no real delay';
  return delta > 0 ? `${formatSpan(delta)} longer` : `${formatSpan(-delta)} shorter`;
}

/**
 * The line under the label.
 *
 * A detection route is described by its turns, because turns are the product —
 * what is being bought is certainty about whether the car behind is following
 * the convoy or the road, and an option that does not force enough of them says
 * so rather than being quietly offered as though it would settle the question.
 * A reroute is described by what it clears, because that is what was asked for,
 * and by what it fails to clear, because an option that leaves two of three
 * threats in place is a different decision from one that leaves none.
 * A correction is described by whether it keeps the scanned road, because the
 * two ways back reach the same place and differ in little else that is visible.
 */
export function optionDetail(option: RouteOption, kind?: SentinelAction): string {
  const cost = optionCost(option);
  if (kind === 'detection_route') {
    return [
      `${option.turns ?? 0} decisive turns`,
      cost,
      option.diagnostic === false ? 'not enough to prove anything' : null,
    ].filter(Boolean).join(' · ');
  }
  if (kind === 'rejoin_route') {
    return [
      option.rejoins_plan ? 'back on the scanned route' : 'roads not scanned',
      cost,
      option.distance != null ? formatMeters(option.distance) : null,
    ].filter(Boolean).join(' · ');
  }
  return [
    option.clears?.length ? `clears ${option.clears.length}` : null,
    option.still_exposed?.length ? `${option.still_exposed.length} still exposed` : null,
    cost,
    option.distance != null ? formatMeters(option.distance) : null,
  ].filter(Boolean).join(' · ');
}

export function isRecommended(option: RouteOption, recommend?: string | null): boolean {
  return !!recommend && optionLabel(option) === recommend;
}

export interface OptionStat {
  label: string;
  value: string;
  tone?: 'good' | 'bad';
}

/** Numbers on the card, so comparing two options does not mean reading two paragraphs. */
export function optionStats(option: RouteOption, kind?: SentinelAction): OptionStat[] {
  const stats: OptionStat[] = [];
  if (kind === 'detection_route' && option.turns != null) {
    stats.push({ label: 'turns', value: String(option.turns), tone: option.diagnostic === false ? 'bad' : 'good' });
  }
  const cost = optionCost(option);
  if (cost) {
    stats.push({ label: 'time', value: cost });
  }
  if (option.delta_distance_m != null && Math.abs(option.delta_distance_m) >= 50) {
    stats.push({
      label: 'distance',
      value: `${option.delta_distance_m > 0 ? '+' : '−'}${formatMeters(Math.abs(option.delta_distance_m))}`,
    });
  } else if (option.distance != null && kind === 'rejoin_route') {
    stats.push({ label: 'distance', value: formatMeters(option.distance) });
  }
  if (kind !== 'detection_route' && kind !== 'rejoin_route') {
    if (option.clears?.length) {
      stats.push({ label: 'clears', value: String(option.clears.length), tone: 'good' });
    }
    if (option.still_exposed?.length) {
      stats.push({ label: 'exposed', value: String(option.still_exposed.length), tone: 'bad' });
    }
  }
  if (kind === 'detection_route' && option.diagnostic === false) {
    stats.push({ label: 'proof', value: 'not enough', tone: 'bad' });
  }
  if (kind === 'rejoin_route') {
    stats.push({
      label: 'road',
      value: option.rejoins_plan ? 'scanned' : 'unscanned',
      tone: option.rejoins_plan ? 'good' : 'bad',
    });
  }
  return stats;
}

export function suggestedAsks(input: {
  followerLabels?: string[];
  openAlerts?: number;
  hasRehearsal?: boolean;
  inProgress?: boolean;
}): string[] {
  const out: string[] = [];
  const name = (input.followerLabels || []).find(Boolean);
  if (name) out.push(`How far is ${name} now?`);
  if (input.inProgress && input.hasRehearsal) {
    out.push('How are we doing against the baseline run?');
  }
  if ((input.openAlerts || 0) > 0) out.push("What's driving the alert count?");
  return out.slice(0, 3);
}

export interface FollowerFact {
  label: string;
  state: string;
  watchlisted: boolean;
  bits: string[];
}

/** Deterministic row under a follow answer — never parsed from the model's prose. */
export function followerFact(row: {
  label?: string | null;
  state?: string | null;
  watchlisted?: boolean;
  distance_from_lead_m?: number | null;
  relation?: string | null;
  distinct_segments?: number | null;
  shared_distance_m?: number | null;
  last_detected?: string | null;
}): FollowerFact {
  const bits: string[] = [];
  if (row.distance_from_lead_m != null) {
    const range = formatMeters(row.distance_from_lead_m);
    bits.push(row.relation ? `${range} ${row.relation}` : range);
  }
  if (row.distinct_segments != null) {
    bits.push(`${row.distinct_segments} segment${row.distinct_segments === 1 ? '' : 's'}`);
  } else if (row.shared_distance_m != null) {
    bits.push(`${formatMeters(row.shared_distance_m)} shared`);
  }
  return {
    label: row.label || 'unknown',
    state: row.state || '',
    watchlisted: !!row.watchlisted,
    bits,
  };
}

export const PENDING_STEPS = [
  'Locating convoy',
  'Gathering open threats',
  'Asking the routing engine',
  'Ranking what came back',
] as const;

export function pendingStepIndex(elapsedMs: number, stepMs = 1600): number {
  if (elapsedMs < 0 || !Number.isFinite(elapsedMs)) return 0;
  return Math.min(PENDING_STEPS.length - 1, Math.floor(elapsedMs / stepMs));
}

export function optionKey(turnId: number, option: RouteOption): string {
  return `${turnId}:${option.label || option.index || 0}`;
}

/**
 * A correction is only a decision while the convoy is off the line.
 * Once it finds the plan again the offer is stale, whether anyone clicked.
 */
export function isStaleRejoin(
  kind: SentinelAction | undefined,
  offRoute: boolean | undefined,
): boolean {
  return kind === 'rejoin_route' && !offRoute;
}
